import React, { useState, useMemo, useRef, useCallback } from 'react';
import { VehiclePowertrain } from '../../types';
import { BoltIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTextInput } from '../../hooks/useTextInput';
import { useModal } from '../../hooks/useModal';

interface PowertrainSelectorProps {
  value?: VehiclePowertrain | null;
  onChange: (powertrain?: VehiclePowertrain | null) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  error?: string;
}

interface PowertrainOption {
  value: VehiclePowertrain;
  label: string;
  description: string;
}

const POWERTRAIN_OPTIONS: PowertrainOption[] = [
  {
    value: VehiclePowertrain.GASOLINE,
    label: 'ガソリン',
    description: 'ガソリンエンジン',
  },
  {
    value: VehiclePowertrain.DIESEL,
    label: 'ディーゼル',
    description: 'ディーゼルエンジン',
  },
  {
    value: VehiclePowertrain.HYBRID,
    label: 'ハイブリッド',
    description: '従来型ハイブリッドシステム',
  },
  {
    value: VehiclePowertrain.SERIES_HYBRID,
    label: 'シリーズHV',
    description: 'シリーズハイブリッドシステム',
  },
  {
    value: VehiclePowertrain.MILD_HYBRID,
    label: 'マイルドHV',
    description: 'マイルドハイブリッドシステム',
  },
  {
    value: VehiclePowertrain.PLUGIN_HYBRID,
    label: 'プラグインHV',
    description: 'プラグインハイブリッドシステム',
  },
  {
    value: VehiclePowertrain.ELECTRIC,
    label: '電気',
    description: '電気自動車（EV）',
  },
];

export const PowertrainSelector: React.FC<PowertrainSelectorProps> = ({
  value,
  onChange,
  placeholder = 'パワートレインを選択してください',
  label,
  required = false,
  disabled = false,
  className = '',
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 統一入力処理フック（検索クエリ用）
  const searchInput = useTextInput({
    initialValue: searchQuery,
    onValueChange: value => {
      setSearchQuery(value);
    },
  });

  // 統一モーダル管理フック（ドロップダウン用）
  useModal({
    isOpen: isOpen,
    onClose: () => {
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue('');
    },
    containerRef: dropdownRef,
    closeOnOverlayClick: true,
    closeOnEscape: true,
  });

  // 検索フィルタリング
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return POWERTRAIN_OPTIONS;

    const query = searchQuery.toLowerCase();
    return POWERTRAIN_OPTIONS.filter(
      option =>
        option.label.toLowerCase().includes(query) ||
        option.description.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // 現在選択されているオプションを取得
  const selectedOption = useMemo(() => {
    return value ? POWERTRAIN_OPTIONS.find(option => option.value === value) : null;
  }, [value]);

  const handleOptionSelect = useCallback(
    (option: PowertrainOption) => {
      onChange(option.value);
      setIsOpen(false);
      setSearchQuery('');
      searchInput.setValue(''); // 統一入力フックもリセット
    },
    [onChange, searchInput]
  );

  const handleClear = () => {
    onChange(null);
    setIsOpen(false);
    setSearchQuery('');
    searchInput.setValue(''); // 統一入力フックもリセット
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      <div className='relative' ref={dropdownRef}>
        {/* セレクタートリガー */}
        <div className='relative'>
          <button
            type='button'
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`
                          w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-700 border rounded-lg shadow-sm 
                          focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-primary-500
                          min-h-[44px] touch-target-input text-gray-900 dark:text-white
                          ${!error ? 'border-gray-300 dark:border-gray-600' : 'border-red-300 dark:border-red-600'}
                          ${disabled ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'hover:border-gray-400 dark:hover:border-gray-500'}
                          transition-colors duration-200
                      `}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <BoltIcon className='h-5 w-5 text-gray-400 mr-3' />
                <span
                  className={
                    selectedOption
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-500 dark:text-gray-400'
                  }
                >
                  {selectedOption ? selectedOption.label : placeholder}
                </span>
              </div>
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </button>
        </div>

        {/* ドロップダウンメニュー */}
        {isOpen && !disabled && (
          <div className='absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden'>
            {/* 検索ボックス */}
            <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
              <div className='relative'>
                <MagnifyingGlassIcon className='h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2' />
                <input
                  type='text'
                  {...searchInput.inputProps}
                  placeholder='パワートレインを検索...'
                  className='w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
                />
              </div>
            </div>

            {/* オプションリスト */}
            <div className='max-h-64 overflow-y-auto'>
              {/* クリアオプション */}
              {selectedOption && (
                <button
                  type='button'
                  onClick={handleClear}
                  className='w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                >
                  選択をクリア
                </button>
              )}

              {/* パワートレインオプション */}
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => (
                  <button
                    key={option.value}
                    type='button'
                    onClick={() => handleOptionSelect(option)}
                    className={`
                                        w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                                        ${
                                          selectedOption?.value === option.value
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                            : 'text-gray-900 dark:text-white'
                                        }
                                    `}
                  >
                    <div className='flex items-center'>
                      <BoltIcon className='h-4 w-4 text-gray-400 mr-3' />
                      <div>
                        <div className='font-medium'>{option.label}</div>
                        <div className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                          {option.description}
                        </div>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className='px-4 py-3 text-gray-500 dark:text-gray-400 text-center'>
                  {searchQuery
                    ? '検索結果が見つかりません'
                    : '利用可能なパワートレインがありません'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {error && <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{error}</p>}
    </div>
  );
};

export default PowertrainSelector;
